import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import CopyButton from '../commonUI/CopyButton';
import GenericDataGrid from '../dataGridTables/GenericDataGrid';
import {
  selectEndpoint,
  selectFullURL,
  selectSearchValue,
  setAPIExplorerNumRowsSelected,
  setNumAPIItems,
} from './apiExplorerSlice';
import APIExplorerHeader from './components/APIExplorerHeader';
import NoRowsOverlayAPIExplorer from './components/NoRowsOverlayAPIExplorer';
import useGetAPIExplorerExportFilename from './hooks/useGetAPIExplorerExportFilename';
import { useTrackCounts } from './hooks/useTrackCounts';
import { createLoadingColumns } from './utils/createLoadingColumns';
import { fetchPaginatedData } from './utils/fetchPaginatedData';

const loadingColumns = createLoadingColumns(5, 'Loading...');

const placeholder = '--';

/**
 * APIExplorer Component
 */
const APIExplorer: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.auth.apiToken);
  const fullURL = useAppSelector(selectFullURL);
  const searchValue = useAppSelector(selectSearchValue);
  const endpoint = useAppSelector(selectEndpoint);
  const noEndpointSelected = endpoint === '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const filename = useGetAPIExplorerExportFilename();

  // Fetch data when the fullUrl changes
  useEffect(() => {
    setLoading(true);
    setError(null);
    setData([]);

    fetchPaginatedData(fullURL, 1000, accessToken, noEndpointSelected, true)
      .then((items) => {
        setData(items);
        setLoading(false);

        // Update total count when data is loaded
        dispatch(
          setNumAPIItems({
            filtered: items.length,
            total: items.length,
          })
        );
      })
      .catch((err) => {
        let errorMessage = 'Error fetching data';
        if (err instanceof Error && err.message) {
          errorMessage += `: ${err.message}`;
        }
        setError(errorMessage);
        setLoading(false);
      });
  }, [dispatch, accessToken, fullURL, noEndpointSelected]);

  // Dynamically generate grid columns based on the keys in the data
  const columns = useMemo(() => {
    if (data.length === 0) return [];
    const keys = new Set<string>();
    data.forEach((item) => {
      Object.keys(item).forEach((key) => keys.add(key));
    });
    return Array.from(keys).map((key) => ({
      field: key,
      headerName: key,
      flex: 1,
      minWidth: 150,
      filterable: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      valueFormatter: (params: any) => {
        // Check if params is an object with a 'value' property; if not, treat params as the value.
        const cellValue =
          params && params.value !== undefined ? params.value : params;

        if (cellValue === undefined || cellValue === null) return placeholder;

        if (typeof cellValue === 'object') {
          try {
            return JSON.stringify(cellValue);
          } catch (e) {
            return placeholder;
          }
        }
        return String(cellValue);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      renderCell: (params: any) => {
        if (!params) return placeholder;
        const value = params.value;
        let text: string;
        if (value === undefined || value === null) {
          text = placeholder;
        } else if (typeof value === 'object') {
          try {
            text = JSON.stringify(value);
          } catch (e) {
            text = placeholder;
          }
        } else {
          text = String(value);
        }
        return (
          <Tooltip
            title={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  p: '0.5rem',
                }}
              >
                <CopyButton textToCopy={text} />
                <Typography variant='body2'>{text}</Typography>
              </Box>
            }
            placement='bottom-start'
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: theme.vars.palette.background.default,
                },
              },
              arrow: {
                sx: {
                  color: theme.vars.palette.background.default,
                },
              },
            }}
          >
            <span>{text}</span>
          </Tooltip>
        );
      },
    }));
  }, [data]);

  // Filter by search term first
  const searchFilteredRows = useMemo(() => {
    const rows = data.map((row, index) => ({
      id: row.id !== undefined && row.id !== null ? row.id : index,
      ...row,
    }));

    // If no search value, return all rows
    if (!searchValue) return rows;

    // Filter rows based on search value
    return rows.filter((row) => {
      const searchTermLower = searchValue.toLowerCase();

      // Check all properties in the row
      // eslint-disable-next-line
      return Object.entries(row).some(([_, value]) => {
        if (value === null || value === undefined) return false;

        // Handle objects by converting to string
        if (typeof value === 'object') {
          try {
            return JSON.stringify(value)
              .toLowerCase()
              .includes(searchTermLower);
          } catch {
            return false;
          }
        }

        // Handle primitive values
        return String(value).toLowerCase().includes(searchTermLower);
      });
    });
  }, [data, searchValue]);

  useTrackCounts(loading, data.length, searchFilteredRows.length);

  return (
    <Box sx={{ width: '100%', height: '100%', p: '1.5rem' }}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
          border: `1px solid ${theme.vars.palette.divider}`,
          borderRadius: '1rem',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr',
        }}
      >
        <GenericDataGrid
          rows={searchFilteredRows}
          columns={loading ? loadingColumns : columns}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          toolbar={APIExplorerHeader}
          selectedRowCountDispatchFunction={setAPIExplorerNumRowsSelected}
          noRowsOverlay={() => <NoRowsOverlayAPIExplorer error={error} />}
          csvOptions={{
            allColumns: true,
            fileName: filename,
            utf8WithBom: true,
          }}
        />
      </Box>
    </Box>
  );
};

export default APIExplorer;
